import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { uploadService } from '../services/api';

/**
 * ImageUploader - Componente para subir imágenes a Cloudflare R2
 * 
 * @param {string} agencyId - ID de la agencia
 * @param {string} uploadType - Tipo de upload: 'agency_logo', 'agency_banner', 'vehicle_cover', 'vehicle_image'
 * @param {string} vehicleId - ID del vehículo (solo para uploads de vehículos)
 * @param {number} imageIndex - Índice de imagen 1-7 (solo para vehicle_image)
 * @param {string} currentImageUrl - URL de la imagen actual (si existe)
 * @param {function} onUploadComplete - Callback cuando se completa el upload
 * @param {string} aspectRatio - Ratio de aspecto para el preview
 * @param {string} label - Etiqueta para mostrar
 */
const ImageUploader = ({
  agencyId,
  uploadType,
  vehicleId = null,
  imageIndex = null,
  currentImageUrl = null,
  onUploadComplete,
  aspectRatio = '1/1',
  label = 'Subir imagen'
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Convertir imagen a WebP usando canvas, con redimensionado si es muy grande
  const convertToWebP = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const MAX_DIMENSION = 1920;
        let { width, height } = img;

        // Redimensionar si alguna dimensión supera el máximo (fotos de cámara móvil)
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob && blob.type === 'image/webp') {
              resolve(blob);
            } else {
              // iOS Safari no soporta WebP en canvas — fallback a JPEG
              canvas.toBlob(
                (jpegBlob) => {
                  if (jpegBlob) resolve(jpegBlob);
                  else reject(new Error('Error al convertir imagen'));
                },
                'image/jpeg',
                0.85
              );
            }
          },
          'image/webp',
          0.85
        );
      };
      img.onerror = () => reject(new Error('Error al cargar imagen'));
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const handleUpload = useCallback(async (file) => {
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    // Límite generoso antes de comprimir: fotos de iPhone pueden pesar 10-15MB
    // La conversión a WebP reducirá el tamaño considerablemente
    const maxRawSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxRawSize) {
      toast.error('La imagen es demasiado grande (máx 20MB)');
      return;
    }

    setUploading(true);
    setProgress(10);

    try {
      // Convertir a WebP
      setProgress(20);
      const webpBlob = await convertToWebP(file);
      
      // Subir via proxy del backend
      setProgress(40);
      const result = await uploadService.uploadFile(
        webpBlob,
        uploadType,
        agencyId,
        vehicleId,
        imageIndex,
        (progressEvent) => {
          const percent = 40 + Math.round((progressEvent.loaded / progressEvent.total) * 55);
          setProgress(percent);
        }
      );

      setProgress(100);
      setPreviewUrl(result.public_url);
      
      if (onUploadComplete) {
        onUploadComplete(result.public_url, result.object_key);
      }

      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [agencyId, uploadType, vehicleId, imageIndex, convertToWebP, onUploadComplete]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    if (onUploadComplete) {
      onUploadComplete(null, null);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      <label className="text-sm font-medium mb-2 block">{label}</label>
      
      <div
        className={`
          relative border-2 border-dashed rounded-lg transition-all
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}
          ${previewUrl ? 'p-2' : 'p-6'}
        `}
        style={{ aspectRatio: previewUrl ? 'auto' : aspectRatio }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid={`image-uploader-${uploadType}`}
      >
        {previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-auto rounded-lg object-cover"
              style={{ maxHeight: '200px' }}
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={handleRemove}
              type="button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center cursor-pointer h-full"
            onClick={triggerFileInput}
          >
            {uploading ? (
              <>
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-2" />
                <p className="text-sm text-slate-600">Subiendo... {progress}%</p>
                <div className="w-32 h-2 bg-slate-200 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="rounded-full bg-slate-100 p-3 mb-3">
                  <Upload className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-600 text-center">
                  Arrastra una imagen o haz clic para seleccionar
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  PNG, JPG, WebP (máx 20MB)
                </p>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

/**
 * VehicleImageGallery - Componente para subir múltiples imágenes de vehículo
 */
export const VehicleImageGallery = ({
  agencyId,
  vehicleId,
  currentImages = [],
  coverUrl = null,
  onImagesChange,
  maxImages = 7
}) => {
  const [images, setImages] = useState(currentImages);
  const [cover, setCover] = useState(coverUrl);

  const handleCoverChange = (url) => {
    setCover(url);
    if (onImagesChange) {
      onImagesChange({ coverUrl: url, images });
    }
  };

  const handleImageChange = (index, url) => {
    const newImages = [...images];
    if (url) {
      newImages[index] = url;
    } else {
      newImages.splice(index, 1);
    }
    setImages(newImages);
    if (onImagesChange) {
      onImagesChange({ coverUrl: cover, images: newImages });
    }
  };

  return (
    <div className="space-y-6">
      {/* Cover Image */}
      <ImageUploader
        agencyId={agencyId}
        uploadType="vehicle_cover"
        vehicleId={vehicleId}
        currentImageUrl={cover}
        onUploadComplete={handleCoverChange}
        aspectRatio="16/9"
        label="Imagen de portada"
      />

      {/* Gallery Images */}
      <div>
        <label className="text-sm font-medium mb-2 block">
          Galería de imágenes ({images.length}/{maxImages})
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: maxImages }).map((_, index) => (
            <ImageUploader
              key={index}
              agencyId={agencyId}
              uploadType="vehicle_image"
              vehicleId={vehicleId}
              imageIndex={index + 1}
              currentImageUrl={images[index] || null}
              onUploadComplete={(url) => handleImageChange(index, url)}
              aspectRatio="4/3"
              label={`Imagen ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
